import React from 'react';
import { FormValue, useField } from 'informed';

import { useInstitute } from '../../hooks/useInstitutes';
import {
  getInstituteDetailsFromInstituteId,
  getInstituteIdFromInstituteName
} from '../../utils/instituteUtils';

import { InstitutesComboBox } from '../InstitutesComboBox';
import { InstituteComboBoxProps } from '../InstitutesComboBox/InstituteComboBox';

import { FieldContainer } from './styledComponents';

export interface InformedInstitutesComboboxProps
  extends InstituteComboBoxProps {
  isHidden?: boolean;
  field: string;
  supportingField: string;
  validationSchemaObject: any;
  touched: boolean;
  isDisabled?: boolean;
  reRenderObjects?: Array<any>;
  fieldWrapperStyles?: React.CSSProperties;
  initialInstituteValue?: string;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  enableDataMask?: boolean;
  hintClassName?: string;
}

export const InformedInstitutesCombobox = (
  props: InformedInstitutesComboboxProps
) => {
  const {
    isHidden,
    field,
    supportingField,
    validationSchemaObject,
    fieldWrapperStyles,
    touched,
    onSelectionChange,
    isDisabled,
    label,
    error,
    onBlur: onBlurPropFunc,
    reRenderObjects,
    initialInstituteValue,
    validateOnBlur = true,
    validateOnChange = true,
    enableDataMask,
    hintClassName,
    ...comboboxProps
  } = props;

  const { fieldState, render, informed } = useField<FormValue<string>>({
    field,
    validateOnBlur: false,
    validateOnChange: validateOnChange,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    isDisabled,
    label,
    ...reRenderObjects
  });

  const { fieldApi } = useField<FormValue<string>>({ field: supportingField });

  const { value, error: fieldError } = fieldState;
  const { onChange, onBlur } = informed;

  const onBlurFunction = (e) => {
    onBlur(e.target.value);
    onBlurPropFunc && onBlurPropFunc(e.target.value);
  };

  const { institutes } = useInstitute();

  const onSelectionChangeFunc = (value) => {
    const institute = getInstituteDetailsFromInstituteId(
      value,
      institutes.current
    );

    if (institute) {
      if (value === 'Other') {
        const { setValue } = fieldApi;
        setValue(institute.institute_name);
        onChange('Other');
      } else {
        onChange(institute.institute_name);
      }

      onSelectionChange && onSelectionChange(institute?.institute_id);
    } else {
      const { setValue } = fieldApi;
      setValue('');
      onChange('');
      onSelectionChange && onSelectionChange('');
    }
  };

  const getSelectedKey = (): string => {
    if (value === 'Other') {
      return value;
    } else {
      return getInstituteIdFromInstituteName(value, institutes.current);
    }
  };

  const renderStatesCombobox = () => {
    return (
      <InstitutesComboBox
        label={label}
        selectedKey={getSelectedKey()}
        onBlur={onBlurFunction}
        isDisabled={isDisabled}
        onSelectionChange={onSelectionChangeFunc}
        error={fieldError}
        initialInstituteValue={initialInstituteValue}
        enableDataMask={enableDataMask}
        hintClassName={hintClassName}
        {...comboboxProps}
      />
    );
  };

  return render(
    <FieldContainer isHidden={isHidden} css={fieldWrapperStyles}>
      {renderStatesCombobox()}
    </FieldContainer>
  );
};
